import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { GET_PINNED_TEMPLATES } from '../../contracts';
import { getPinnedTemplates } from '../api/templates';
import { getTemplatesDataConfig } from '../config';

/**
 * Module-scope invalidator — runs outside a hook, so it takes the app's
 * queryClient singleton from the `configureTemplatesData` seam.
 */
export const invalidatePinnedTemplates = () =>
  getTemplatesDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_PINNED_TEMPLATES],
  });

/**
 * The pinned-template list (D2.7, ARCFE-1046).
 *
 * `isPinnedNotegenUiEnabled` crosses as a VALUE, and that is a measured
 * decision rather than the default. ARCFE-1032 made two templates flags cross
 * as GETTERS because they resolved through `posthog.isFeatureEnabled`, which
 * emits a `$feature_flag_called` exposure at the moment it is read — so
 * eager resolution would have fired extra exposures. This flag is different:
 * `useIsPinnedTemplateNotegenUiEnabled` is an OpenFeature `useBooleanFlagValue`
 * hook that already resolves at render whether or not a fetch happens, so there
 * is no exposure to defer and a getter would buy nothing. `queryFn` closes over
 * the render-time value exactly as the pre-move body did.
 *
 * The `enabled` expression keeps its exact original shape: an explicit
 * `options.enabled` is ANDed with auth, and an absent one falls back to auth
 * alone — which is not the same as defaulting it to `true`, because
 * `enabled: false` must still win.
 */
export function useQueryPinnedTemplates(
  options: { enabled?: boolean } | undefined,
  isAuthenticated: boolean,
  isPinnedNotegenUiEnabled: boolean,
  onRequestError: (error: AxiosError) => void
) {
  return useQuery({
    enabled:
      options?.enabled !== undefined
        ? options.enabled && isAuthenticated
        : isAuthenticated,
    queryKey: [GET_PINNED_TEMPLATES],
    queryFn: async () => {
      try {
        const { data } = await getPinnedTemplates();

        // When FF is on and all templates have a BE sort order, trust it.
        // Otherwise, fall back to client-side sort by added_to_pinned_at
        // so existing users' pinned tabs carry over in the same order.
        const allHaveSortOrder = data.templates.every((t) => t.pinned_sort_order != null);

        if (isPinnedNotegenUiEnabled && allHaveSortOrder) {
          return data.templates;
        }

        return data.templates.sort((a, b) => {
          const dateA = a.added_to_pinned_at
            ? new Date(a.added_to_pinned_at).getTime()
            : 0;
          const dateB = b.added_to_pinned_at
            ? new Date(b.added_to_pinned_at).getTime()
            : 0;
          return dateA - dateB;
        });
      } catch (error) {
        if (error instanceof AxiosError) {
          if (getTemplatesDataConfig().isOfflineNetworkError(error)) return;

          onRequestError(error);
        }
      }
    },
  });
}
